import { useEffect, useRef, useState, useCallback } from 'react';
import type { HotStreakState, RaceCardResult, RaceFoldResult, MascotId, RacingCardType } from '@flip7/shared';
import { HS_MASCOTS, HS_TRACK_CONFIG } from '@flip7/shared';

interface Props {
  state: HotStreakState;
  playerId: string | null;
  lastCard: RaceCardResult | null;
  lastFold: RaceFoldResult | null;
}

const MASCOT_EMOJI: Record<MascotId, string> = {
  hurley: '🌭',
  gobbler: '🐻',
  dangle: '🐟',
  mum: '👑',
};

interface CardAction {
  icon: string;
  label: string;
  color: string;
  isBig: boolean;
}

function getCardAction(card: RaceCardResult['card']): CardAction {
  const mascotName = card.mascot === 'all' ? 'ALL' : HS_MASCOTS[card.mascot]?.name ?? '?';
  const mascotColor = card.mascot === 'all' ? '#4ade80' : HS_MASCOTS[card.mascot]?.color ?? '#999';

  switch (card.type) {
    case 'number': {
      const v = card.value ?? 0;
      return {
        icon: v > 0 ? '⚡' : '⏪',
        label: `${mascotName} ${v > 0 ? '+' : ''}${v}`,
        color: v > 0 ? mascotColor : '#f87171',
        isBig: Math.abs(v) >= 3,
      };
    }
    case 'star':
      return { icon: '⭐', label: `${mascotName} STAR JUMP`, color: '#fbbf24', isBig: true };
    case 'turn-around':
      return { icon: '🔄', label: `${mascotName} TURNS AROUND`, color: '#a78bfa', isBig: true };
    case 'fall-down':
      return { icon: '💥', label: `${mascotName} FALLS DOWN!`, color: '#ef4444', isBig: true };
    case 'swerve-left':
    case 'swerve-right':
      return { icon: '↔️', label: `${mascotName} SWERVES`, color: '#38bdf8', isBig: false };
    case 'recover':
      return { icon: '💪', label: `${mascotName} RECOVERS!`, color: '#34d399', isBig: true };
    case 'multi': {
      const effect = card.multiEffect?.type ?? 'number';
      const effectLabel: Record<string, string> = {
        number: `ALL MOVE ${card.multiEffect?.value ?? ''}`,
        recover: 'ALL RECOVER',
        star: 'ALL STAR JUMP',
      };
      return { icon: '🟢', label: effectLabel[effect] ?? 'ALL MOVE', color: '#4ade80', isBig: true };
    }
    default:
      return { icon: '🃏', label: mascotName, color: '#999', isBig: false };
  }
}

/** Commentary callouts for dramatic events */
function getCommentary(result: RaceCardResult): string | null {
  for (const event of result.events) {
    if (event.type === 'dq-knockout') return `${MASCOT_EMOJI[event.mascot!]} KNOCKOUT! ${HS_MASCOTS[event.mascot!]?.name} IS OUT!`;
    if (event.type === 'dq-out-of-bounds') return `${MASCOT_EMOJI[event.mascot!]} OUT OF BOUNDS!`;
    if (event.type === 'collision') return `💥 COLLISION!`;
    if (event.type === 'finish') return `🏁 ${HS_MASCOTS[event.mascot!]?.name} FINISHES!`;
  }
  return null;
}

export function HotStreakRace({ state, playerId, lastCard, lastFold }: Props) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [activeCard, setActiveCard] = useState<{ action: CardAction; key: number } | null>(null);
  const [commentary, setCommentary] = useState<string | null>(null);
  const [foldFlash, setFoldFlash] = useState(false);
  const cardCounter = useRef(0);

  // Dramatic card reveal sequence
  useEffect(() => {
    if (!lastCard) return;

    const action = getCardAction(lastCard.card);
    cardCounter.current += 1;
    const key = cardCounter.current;

    // Stage 1: Card slams in
    setActiveCard({ action, key });
    setCommentary(null);

    // Stage 2: After card display, show commentary for big events
    const commentaryTimer = setTimeout(() => {
      const text = getCommentary(lastCard);
      if (text) setCommentary(text);
    }, 600);

    // Stage 3: Card fades out
    const clearTimer = setTimeout(() => {
      setActiveCard(null);
      setCommentary(null);
    }, 1300);

    return () => {
      clearTimeout(commentaryTimer);
      clearTimeout(clearTimer);
    };
  }, [lastCard]);

  // Track fold flash
  useEffect(() => {
    if (!lastFold) return;
    setFoldFlash(true);
    setCommentary('🌊 TRACK FOLDS!');
    const t = setTimeout(() => {
      setFoldFlash(false);
      setCommentary(null);
    }, 1500);
    return () => clearTimeout(t);
  }, [lastFold]);

  // Auto-scroll to follow leading mascot
  useEffect(() => {
    if (!trackRef.current) return;
    const leading = [...state.mascots]
      .filter((m) => m.status === 'running' || m.status === 'fallen')
      .sort((a, b) => b.position - a.position)[0];
    if (leading) {
      const scrollTarget = (leading.position / HS_TRACK_CONFIG.finishLine) * trackRef.current.scrollWidth;
      trackRef.current.scrollTo({ left: scrollTarget - 200, behavior: 'smooth' });
    }
  }, [state.mascots]);

  const trackWidth = HS_TRACK_CONFIG.finishLine + 2;
  const spaceWidth = 48;

  return (
    <div className={`hs-race ${foldFlash ? 'hs-fold-flash' : ''}`}>
      {/* Race header */}
      <div className="hs-race-header">
        <span className="hs-race-title">RACE {state.currentRace}</span>
        <div className="hs-deck-counter">
          <span className="hs-deck-icon">🃏</span>
          <span className="hs-deck-count">{state.deckRemaining}</span>
        </div>
      </div>

      {/* ═══ DRAMATIC CARD REVEAL ═══ */}
      <div className="hs-card-stage">
        {activeCard && (
          <div
            key={activeCard.key}
            className={`hs-card-reveal ${activeCard.action.isBig ? 'hs-card-big' : ''}`}
          >
            <div
              className="hs-card-face"
              style={{
                '--card-color': activeCard.action.color,
                borderColor: activeCard.action.color,
              } as React.CSSProperties}
            >
              <span className="hs-card-icon">{activeCard.action.icon}</span>
              <span className="hs-card-label">{activeCard.action.label}</span>
            </div>
          </div>
        )}

        {/* Commentary callout */}
        {commentary && (
          <div className="hs-commentary" key={commentary}>
            {commentary}
          </div>
        )}
      </div>

      {/* ═══ RACE TRACK ═══ */}
      <div className="hs-track-container" ref={trackRef}>
        <div className="hs-track" style={{ width: trackWidth * spaceWidth }}>
          {/* Track grid lines */}
          <div className="hs-track-grid">
            {Array.from({ length: trackWidth }, (_, i) => (
              <div
                key={i}
                className={`hs-track-cell
                  ${i === HS_TRACK_CONFIG.finishLine ? 'hs-cell-finish' : ''}
                  ${HS_TRACK_CONFIG.starPositions.includes(i) ? 'hs-cell-star' : ''}
                  ${i < state.track.foldPosition ? 'hs-cell-folded' : ''}
                  ${HS_TRACK_CONFIG.foldLines.includes(i) ? 'hs-cell-foldline' : ''}
                `}
                style={{ width: spaceWidth }}
              >
                {i === HS_TRACK_CONFIG.finishLine && <div className="hs-finish-marker">🏁</div>}
                {HS_TRACK_CONFIG.starPositions.includes(i) && i >= state.track.foldPosition && (
                  <div className="hs-star">★</div>
                )}
              </div>
            ))}
          </div>

          {/* Mascot runners */}
          {state.mascots.map((mascot) => {
            const info = HS_MASCOTS[mascot.id];
            const isActive = lastCard?.card.mascot === mascot.id || lastCard?.card.mascot === 'all';
            return (
              <div key={mascot.id} className={`hs-runner-lane hs-lane-${mascot.lane}`}>
                <div
                  className={`hs-runner
                    hs-runner--${mascot.status}
                    ${mascot.facing === 'backward' ? 'hs-runner--backwards' : ''}
                    ${isActive && activeCard ? 'hs-runner--active' : ''}
                  `}
                  style={{
                    left: mascot.position * spaceWidth,
                    '--runner-color': info.color,
                  } as React.CSSProperties}
                >
                  <div className="hs-runner-body">
                    <span className="hs-runner-emoji">{MASCOT_EMOJI[mascot.id]}</span>
                  </div>
                  {mascot.status === 'fallen' && <span className="hs-runner-fx">💫</span>}
                  {mascot.status === 'dq' && <span className="hs-runner-fx hs-runner-dq">✖</span>}
                  {mascot.status === 'finished' && <span className="hs-runner-fx hs-runner-win">🏆</span>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Mascot status bar with bet highlighting */}
      <div className="hs-status-bar">
        {(() => {
          // Compute live positions (rank by position descending, finished first)
          const ranked = [...state.mascots].sort((a, b) => {
            if (a.status === 'finished' && b.status !== 'finished') return -1;
            if (b.status === 'finished' && a.status !== 'finished') return 1;
            if (a.status === 'dq' && b.status !== 'dq') return 1;
            if (b.status === 'dq' && a.status !== 'dq') return -1;
            return b.position - a.position;
          });

          // Player's bet mascots
          const myBets = playerId
            ? state.players.find((p) => p.id === playerId)?.bets ?? []
            : [];
          const betMascots = new Set(myBets.filter((b) => b.ticket.mascot).map((b) => b.ticket.mascot!));

          return state.mascots.map((mascot) => {
            const info = HS_MASCOTS[mascot.id];
            const finish = state.finishOrder.find((f) => f.mascot === mascot.id);
            const liveRank = ranked.findIndex((m) => m.id === mascot.id) + 1;
            const posLabel = finish
              ? `${['1st', '2nd', '3rd', '4th'][finish.position - 1]}${finish.finishType === 'dq' ? ' DQ' : ''}`
              : `${['1st', '2nd', '3rd', '4th'][liveRank - 1]}`;
            const hasBet = betMascots.has(mascot.id);

            return (
              <div
                key={mascot.id}
                className={`hs-status-chip hs-status-chip--${mascot.status} ${hasBet ? 'hs-status-chip--bet' : ''}`}
                style={{ '--chip-color': info.color } as React.CSSProperties}
              >
                <span className="hs-chip-emoji">{MASCOT_EMOJI[mascot.id]}</span>
                <span className="hs-chip-name">{info.name}</span>
                <span className={`hs-chip-pos ${hasBet ? 'hs-chip-pos--bet' : ''}`}>{posLabel}</span>
                {hasBet && <span className="hs-chip-bet-marker">🎫</span>}
              </div>
            );
          });
        })()}
      </div>

      {/* Your bets panel — visible during race */}
      {playerId && (() => {
        const myBets = state.players.find((p) => p.id === playerId)?.bets ?? [];
        if (myBets.length === 0) return null;

        // Compute live ranking for payout preview
        const ranked = [...state.mascots].sort((a, b) => {
          if (a.status === 'finished' && b.status !== 'finished') return -1;
          if (b.status === 'finished' && a.status !== 'finished') return 1;
          if (a.status === 'dq' && b.status !== 'dq') return 1;
          if (b.status === 'dq' && a.status !== 'dq') return -1;
          return b.position - a.position;
        });

        return (
          <div className="hs-race-bets">
            <h4>Your Bets</h4>
            {myBets.map((bet, i) => {
              if (bet.ticket.type === 'mascot' && bet.ticket.mascot) {
                const mascotId = bet.ticket.mascot;
                const info = HS_MASCOTS[mascotId];
                const liveRank = ranked.findIndex((m) => m.id === mascotId) + 1;
                const finish = state.finishOrder.find((f) => f.mascot === mascotId);
                const posLabel = finish
                  ? ['1st', '2nd', '3rd', '4th'][finish.position - 1]
                  : ['1st', '2nd', '3rd', '4th'][liveRank - 1];
                const payout = bet.side === 'safe' ? bet.ticket.safePayout : bet.ticket.riskyPayout;
                const tp = payout as { first: number; second: number; third: number };
                const currentPayout = finish
                  ? [tp.first, tp.second, tp.third, 0][finish.position - 1]
                  : [tp.first, tp.second, tp.third, 0][liveRank - 1];

                return (
                  <div key={i} className="hs-race-bet" style={{ borderColor: info.color }}>
                    <span className="hs-race-bet-mascot">
                      {MASCOT_EMOJI[mascotId]} {info.name}
                    </span>
                    <span className={`hs-race-bet-pos hs-race-bet-pos--${liveRank}`}>
                      {posLabel}
                    </span>
                    <span className={`hs-race-bet-payout ${currentPayout > 0 ? 'positive' : ''}`}>
                      ${currentPayout}{bet.isDoubled ? ' x2' : ''}
                    </span>
                    <span className={`hs-race-bet-side ${bet.side}`}>{bet.side}</span>
                  </div>
                );
              }
              // Side bet
              return (
                <div key={i} className="hs-race-bet hs-race-bet--side">
                  <span className="hs-race-bet-question">{bet.ticket.question}</span>
                  <span className={`hs-race-bet-side ${bet.side}`}>{bet.side}</span>
                </div>
              );
            })}
          </div>
        );
      })()}
    </div>
  );
}
